/**
 * PROGRESSIVE LABEL COLLAPSE FOR THE TABLE TOOLBAR (VRG-535).
 *
 * The toolbar row is `search (+ scan) | Download · Views · Filter · Sort · Layout`. Nothing
 * in it can yield — `Search` carries a hard `minWidth: 300` and every button is sized by its
 * own label — so when the row runs out of width the only outcomes are WRAP (two rows) or
 * CLIP. `ActionLine` chose wrap, which is safe but reads as a bug: the button group drops to
 * a line of its own under the search field.
 *
 * This module is the third option. As the container narrows, the buttons shed their LABELS
 * one at a time, left to right, and keep their icons — so the row stays one row for ~130px
 * longer before wrap is reached at all. Wrap stays in place underneath as the final fallback
 * (see `flexWrap` on the `ActionLine` root); this only pushes the point where it triggers.
 *
 * ── WHY CONTAINER QUERIES AND NOT `useMediaQuery` / `ResizeObserver` ──────────────────
 *
 * Two independent constraints rule out the alternatives, and both are load-bearing:
 *
 * 1. THE VIEWPORT IS THE WRONG THING TO MEASURE. The sidebar is not an overlay — it is a
 *    `position: static` 288px flex sibling — so collapsing it hands the toolbar 288 more
 *    pixels at an unchanged viewport width. Every control in this row previously carried
 *    `display: { xs: 'none', md: 'inline' }`, a VIEWPORT query at 900px, which is why the
 *    labels never dropped at the 1280px width the defect was reported at: the viewport was
 *    comfortably over `md` while the container was 943px and two rows tall.
 *
 * 2. OBSERVERS DO NOT RUN IN THE PREVIEW PANE. `ResizeObserver` and `requestAnimationFrame`
 *    fire ZERO times in the Claude preview pane, so an observer-driven implementation would
 *    silently never collapse in the one environment this work is verified in — passing
 *    review by looking exactly like the bug it was meant to fix. Container queries are pure
 *    CSS and evaluate normally there.
 *
 * ── THE MECHANISM WAS ALREADY HERE, ON ANALYTICS (VRG-517) ────────────────────────────
 *
 * `AnalyticsFiltersHeader` solved the same problem for its own row — date / certificate
 * status / scan status controls dropping in priority order — with exactly this technique.
 * That was the first hand-roll; this is the second, which is the extraction trigger
 * (working-conventions §36), so the MECHANISM lives here and Analytics imports it rather
 * than a second copy being written beside it.
 *
 * What is shared: the container declaration, the container NAME, and the `showAtLeast` /
 * `hideAtLeast` helpers. What is NOT shared: the thresholds. Analytics keeps its own next
 * to the measurements they came from, because they describe a different row.
 *
 * ⚠️ ONE CONTAINER NAME FOR BOTH. Analytics used to declare `analytics-toolbar` on the
 * `ActionLine` root via the `sx` prop. `ActionLine` now declares the container itself, so
 * every toolbar in the app is a query container without its caller doing anything — and a
 * second name on the same element would mean whichever `sx` landed last silently won. The
 * name is the shared thing; see `TOOLBAR_CONTAINER_NAME`.
 */

/**
 * The query container every toolbar rule below is written against.
 *
 * Declared by `ActionLine` on its own root, and ALSO by Analytics' applied-filter chip row
 * (`AnalyticsAppliedFilters`), which is a sibling of the same width — a rule written against
 * one resolves identically in the other, which is what keeps a fallback chip from being on
 * screen at the same time as the control it stands in for.
 *
 * A query never matches the element's OWN container, only the nearest ancestor one, so the
 * root declaring it is exactly right: every control inside resolves against the row's width.
 */
export const TOOLBAR_CONTAINER_NAME = 'vera-toolbar'

/**
 * Spread onto an element to make it the query container.
 *
 * `container-type: inline-size` implies `contain: layout style inline-size`. Safe on the
 * `ActionLine` root: it is a block-level flex container whose width comes from its parent,
 * so inline-size containment changes nothing about how it is measured, and every overlay the
 * row hosts — the filter drawer, the sort/layout/views menus, the search typeahead — is
 * portalled to `document.body`, so layout containment has no positioned descendant to
 * recapture.
 *
 * ⚠️ An element declaring `inline-size` containment can no longer be sized BY ITS CONTENTS.
 * That is why the container is the ActionLine ROOT and not the right-hand button group: the
 * group is the thing whose width we are trying to shrink, so querying it would be circular.
 */
export const toolbarContainerSx = {
  containerType: 'inline-size',
  containerName: TOOLBAR_CONTAINER_NAME,
} as const

/** Hidden by default; shown once the container reaches `px`. */
export const showAtLeast = (px: number, shown: string = 'flex') => ({
  display: 'none',
  [`@container ${TOOLBAR_CONTAINER_NAME} (min-width:${px}px)`]: { display: shown },
})

/** The exact inverse — shown by default, hidden once the container reaches `px`. */
export const hideAtLeast = (px: number, shown: string) => ({
  display: shown,
  [`@container ${TOOLBAR_CONTAINER_NAME} (min-width:${px}px)`]: { display: 'none' },
})

/**
 * ── THE LADDER ────────────────────────────────────────────────────────────────────────
 *
 * Each constant is the container width at or above which THAT control keeps its label. They
 * descend in the row's own left-to-right order, so narrowing the container drops labels from
 * the left: Download first, then Views, Filter, Sort, and Layout last.
 *
 * WHY THAT ORDER, AND WHY IT IS NOT ARBITRARY. It is not merely "leftmost first" — the
 * ordering happens to coincide with how much each label is doing:
 *
 *   Download  a one-off ACTION with a universally-read glyph, and the only control here
 *             whose tooltip already carried more than its label ever did (it states whether
 *             the click exports every matching row or only this page). Cheapest to unlabel.
 *   Views     the trigger reads the ACTIVE VIEW'S NAME when one is applied, so its label is
 *             genuinely informative — but that name is already capped at 200px and
 *             ellipsised, and the tooltip carries it in full.
 *   Filter    keeps its count badge when the label goes, so "some filtering is applied"
 *             survives the collapse; only the word goes.
 *   Sort      same, one step later.
 *   Layout    LAST, and deliberately. `TableIcon` is the weakest glyph of the five and
 *             "Layout" names a concept the product is still teaching rather than one the
 *             reader already holds — the same argument `Table.tsx` makes for why the row
 *             cannot go all-icons.
 *
 * ⚠️ THE ORDER STILL HOLDS BUT THE BOTTOM THREE ARE NO LONGER SPREAD OUT. Filter, Sort and
 * Layout now shed within 12px of each other rather than over ~70px, because the `layout` rung
 * is pinned by the both-applied row and the two above it are lifted to keep the ladder
 * descending. See the † note under the numbers. The ordering above is what decides ties and
 * what to re-derive from if the row changes; it is no longer five visibly separate steps.
 *
 * ⚠️ AND AN APPLIED CONTROL DOES NOT WAIT FOR ITS RUNG AT ALL — Filter and Sort drop their
 * labels the moment they carry a count badge, at every width. Their rungs govern only the
 * UNAPPLIED case. See the applied-state section below.
 *
 * ── THE NUMBERS ───────────────────────────────────────────────────────────────────────
 *
 * MEASURED BY BISECTION in the browser at 2px, not derived from control widths. The row is
 * 66px tall at one row and 127px at two, so the crossover is read off the HEIGHT rather than
 * eyeballed. Method: pin a label configuration with inline `display: … !important`, then
 * sweep the row's own width and record where the height changes.
 *
 * Reference configuration — `/certificates`, the busiest Table toolbar: bulk-actions toggle
 * + search + scan on the left, all five buttons on the right.
 *
 * ⚠️ EACH RUNG IS BISECTED AGAINST WHICHEVER OF THE FOUR STATES NEEDS IT WIDEST, not against
 * the empty toolbar. `{no filter, filter} × {no sort, sort}` — see the applied-state section
 * below for why one table can cover all four at all. `fits at ≥` is the worst measured need
 * across the four; `binds` names the state that produced it.
 *
 *   labels still shown            fits at ≥   binds        constant   headroom
 *   all five  (= D·V·L applied)     1026      either        1042        16
 *   V·F·S·L   (= V·L applied)        954      either         966        12
 *   Filter · Sort · Layout           896      unapplied      922        26 †
 *   Sort · Layout                    896      filter         916        20 †
 *   Layout only                      904      BOTH           910         6
 *   none — the FLOOR                 850      BOTH            —         —
 *
 * † MONOTONICITY, not slack. A label must never REAPPEAR as the row narrows, so the rungs
 *   have to descend. `layout` is pinned at 910 by the both-applied row, and `sort` / `filter`
 *   each need only ~898 on their own — so they are lifted above `layout` rather than left
 *   below it. The visible consequence is that the bottom three rungs now sit within 12px of
 *   each other: Filter, Sort and Layout effectively shed their labels together at ~910 rather
 *   than in three separate steps. That is a real cost of keeping ONE table, and it is
 *   accepted — the alternative is a per-state matrix (rejected below), and the width the
 *   staircase collapses at is one nothing reaches at 1280 (see the floor note).
 *
 * ⚠️ `Filter · Sort · Layout` measures 896 here where the first pass of this module recorded
 * 900, on the same page and the same method. The 4px is the `startIcon` margin: pinning a
 * label back ON below its own rung does not restore the margins `toolbarStartIconSx` zeroed,
 * so a pinned configuration is up to 4px narrower per startIcon button than the real one. It
 * does not matter at these headrooms, but do not read a 4px agreement between two runs of
 * this table as confirmation of anything.
 *
 * ⚠️ DO NOT DERIVE THESE FROM BUTTON WIDTHS — a first pass did, and was wrong by ~38px. The
 * arithmetic looks sound: left group 436 + 12px gap + the right group's measured span. It
 * over-predicts because THE LEFT GROUP IS NOT RIGID — `Search` has `minWidth: 300` but a
 * larger natural width, so the row reclaims the difference by shrinking the field before it
 * gives up and wraps. Only a measurement of the assembled row knows that.
 *
 * ⚠️ AND DO NOT MEASURE THROUGH CACHED LABEL NODES. React remounts these (the `Zoom`-wrapped
 * count badges, the `Collapse`-wrapped clear buttons), so a reference captured before the
 * sweep goes stale and the `!important` lands on a DETACHED node — the pin silently does
 * nothing and you measure the live ladder while believing you pinned a configuration. That
 * produced a self-consistent table that disagreed with the end-to-end sweep by up to 72px.
 * Re-query the nodes after every width change.
 *
 * ⚠️ THE `layout` RUNG SHIPPED 6px TOO LOW ON THE FIRST PASS, at 822 against a measured 828,
 * which opened a 4px window (826–822) where the row showed the Layout label AND wrapped —
 * the exact defect this module exists to remove, reintroduced at one stage of its own
 * ladder. It was invisible in a stage-by-stage check and only showed up in a continuous
 * end-to-end sweep. Sweep the whole range, not the boundaries you expect to matter.
 *
 * ⚠️ THE FLOOR IS STATE-DEPENDENT AND THE WORST OF IT IS 850. Measured one-row floors:
 * 774 unapplied, 812 with either one applied, 850 with both. The spread is the count badges —
 * 38px each, and a badge is the one thing that cannot be dropped (a filter silently applied
 * with no indicator is a worse defect than a wrapped row). 1280 with the sidebar EXPANDED
 * gives a 943px container, which clears even the worst floor by 93px; collapsed it is 1151.
 * Below the floor there is nothing left to shed and the row wraps as it did before — the
 * correct behaviour, not a regression: wrap is still the fallback, this ladder only defers it.
 *
 * ⚠️ THE THRESHOLDS ARE TUNED FOR THE FIVE-BUTTON TABLE TOOLBAR, and they are constants, so
 * a surface with a different left group gets a different amount of headroom. This is an
 * accepted limitation of a CSS-only approach — a container query cannot know what else is in
 * the row — and it degrades in the safe direction on both sides: a page with a NARROWER left
 * group (Tags → Shop, which has no bulk-actions toggle) simply keeps its labels a little
 * longer than it strictly needs to, and a page with a WIDER one (Analytics, whose date field
 * is 385px) drops them a little early. Neither wraps, which is the property that matters.
 *
 * ⚠️ RE-MEASURE, DO NOT RE-REASON, if this row gains or loses a control or a label changes.
 * The failure is silent — the controls all still render, the row simply becomes two rows
 * tall again. Re-measure in the WORST state (both applied), not the empty one.
 */
export const TOOLBAR_LABEL_MIN_WIDTH_PX = {
  download: 1042,
  views: 966,
  filter: 922,
  sort: 916,
  layout: 910,
} as const

/**
 * ── THE APPLIED STATE, AND WHY THERE IS STILL ONLY ONE TABLE (VRG-535, second pass) ───
 *
 * A control with a filter or a sort applied grows: it gains a count BADGE and a clear-`X`.
 * Measured on the reference row at a 1300px container, with both Filter and Sort applied,
 * the right-hand group went 588.6px → 750.5px — **+162px**, which is more than the whole
 * ladder's total travel. The first pass of this module was bisected against the EMPTY
 * toolbar and so went on showing the label set it had picked for a much narrower row: at the
 * 943px container a 1280 viewport gives, one applied sort put the row back to two rows, and
 * it stayed two rows continuously from 1112px all the way down past the ladder's floor. The
 * ladder never dropped enough labels to recover, at any width.
 *
 * The obvious repair is a THRESHOLD MATRIX — a second and third table selected by
 * `:has([data-toolbar-applied])` on this container — and it is the wrong one. It triples the
 * bisection burden this block already warns about, it puts a DOM contract between three
 * components that currently share nothing but a container name, and every future control or
 * label change then has to be re-measured three times instead of once.
 *
 * So the growth is removed at the source instead, and both halves are load-bearing:
 *
 *   1. AN APPLIED CONTROL HIDES ITS OWN LABEL, at every width, from its own local state —
 *      no threshold, no shared state, nothing to bisect. The badge takes the label's place.
 *      This is the same judgement the ladder's own ordering already makes one step later
 *      ("Filter keeps its count badge when the label goes, so 'some filtering is applied'
 *      survives the collapse"); with a badge actually present, the word is the redundant
 *      half, and the tooltip and `aria-label` both name the control and its count in full.
 *   2. THE CLEAR-`X` IS THE LADDER'S FIRST RUNG — the row sheds it before it sheds any
 *      label (`TOOLBAR_CLEAR_MIN_WIDTH_PX`). It is the most duplicated thing in the row:
 *      the filter drawer's footer `Clear` and the sort menu's `Clear` are both permanently
 *      visible inside the panel the control opens, so the CLEARING ACTION never becomes
 *      unreachable — only its shortcut does, and only where there is no room for it.
 *
 * Together, with both controls applied, they take the row's overhead from **+162px to +8px**
 * at the wide end — inside a single table's headroom, which is what lets one ladder stay
 * correct in all four states. Bisecting that one table against the WORST of the four makes
 * the other three safe by construction rather than by a second measurement.
 *
 * ⚠️ +8px IS THE WIDE-END NUMBER AND IT DOES NOT HOLD ALL THE WAY DOWN. It is +8 because an
 * applied control (icon + badge, 90px) is about the width of an unapplied LABELLED one
 * (Filter 100, Sort 92) — so where the ladder is still showing labels, the badge costs
 * nothing. Once the ladder has already dropped a control's label, the comparison is against
 * an ICON-ONLY control (52px) and the badge costs its full **+38 each**. That residue is
 * irreducible: it is what moves the one-row floor from 774 to 850, and it is why the bottom
 * rung (`layout`, 910) is the one with only 6px of headroom while the top has 16. Removing it
 * would mean dropping the badge, which is not on the table — an applied filter with no
 * indicator is a worse defect than a wrapped row.
 *
 * ⚠️ NEITHER HALF WORKS ALONE, and the numbers say so rather than the reasoning. Swept
 * end-to-end at 2px with ONE SORT APPLIED, against the FIRST-PASS ladder (1032/960/906/864/
 * 834), the highest container width still wrapping was:
 *
 *   shipped (label kept, X shown)      1112     the defect, and it never recovered: two rows
 *                                               continuously from 1112 down past the floor
 *   (2) alone — X hidden, label kept   1064     the badge alone still exceeds the headroom
 *   (1) alone — label hidden, X shown  1082     the X alone still exceeds it
 *   (1) + (2)                          1034     narrow windows at each rung boundary only
 *
 * ⚠️ NOTE THE LAST ROW IS 1034, NOT ZERO — (1)+(2) shrank the problem from "wrapped at every
 * width" to "wrapped in a 2–6px window at each rung boundary", which is the signature of a
 * rung a few px too low, not of a missing mechanism. Closing it needed the THIRD thing: the
 * table above, re-bisected against the worst state. All three are required; two of them get
 * you a defect that only shows up if you sweep continuously rather than checking the stages
 * you expect to matter.
 *
 * A single-applied control loses its `X` earlier than it strictly needs to, because that rung
 * is set for two. That is the accepted direction (see the left-group note below): a control
 * keeps its shortcut only where the row can afford it for BOTH of them.
 */
export const TOOLBAR_CLEAR_MIN_WIDTH_PX = 1140

export type ToolbarLabelKey = keyof typeof TOOLBAR_LABEL_MIN_WIDTH_PX

/**
 * Spread onto a toolbar button's LABEL element.
 *
 * `shown` is the display the label takes once it is back: `inline` for a bare `<span>`,
 * `block` for one that ellipsises (`text-overflow` needs a block-level box — Views' label
 * is capped at 200px and relies on this).
 *
 * `applied` short-circuits the query entirely: a control carrying a count badge hides its
 * label at EVERY width, because the badge has taken the label's place. See the applied-state
 * section above — this is half of what keeps one threshold table valid in all four states.
 * Deliberately NOT a lower threshold: a rung would be a fifth number to bisect for a rule
 * whose answer never depends on the width.
 */
export const toolbarLabelSx = (
  key: ToolbarLabelKey,
  shown: string = 'inline',
  applied: boolean = false,
) => (applied ? { display: 'none' } : showAtLeast(TOOLBAR_LABEL_MIN_WIDTH_PX[key], shown))

/**
 * Spread onto a toolbar button that uses MUI's `startIcon`, ALONGSIDE `toolbarLabelSx` on
 * its label — and with the SAME `applied` value, or the icon will not centre.
 *
 * `.MuiButton-startIcon` carries `margin-right: 8px; margin-left: -4px` so the icon sits at
 * a readable distance from the text it precedes. With the text `display: none` those margins
 * become dead space on a now icon-only button — the glyph sits visibly off-centre, 12px
 * closer to the right edge than the left. Zeroing them is what makes the collapsed button
 * read as an icon button rather than a labelled one with the label missing.
 *
 * NOT needed for a button that lays its icon and label out as siblings of a `<Stack>` (Views,
 * Layout): `spacing` there emits a `margin-left` on the LABEL, so a `display: none` label
 * takes its own gap out of the flow with it.
 */
export const toolbarStartIconSx = (key: ToolbarLabelKey, applied: boolean = false) => ({
  '& .MuiButton-startIcon': { marginRight: 0, marginLeft: 0 },
  ...(applied
    ? {}
    : {
        [`@container ${TOOLBAR_CONTAINER_NAME} (min-width:${TOOLBAR_LABEL_MIN_WIDTH_PX[key]}px)`]: {
          '& .MuiButton-startIcon': { marginRight: '8px', marginLeft: '-4px' },
        },
      }),
})

/**
 * Spread onto the `<Collapse>` that WRAPS an applied control's clear-`X` — not onto the
 * button, which sits inside a wrapper that has already reserved the full 48px.
 *
 * `block` because that is what `MuiCollapse-horizontal` computes to (measured; it is not
 * `flex`, which is what the horizontal orientation reads like it ought to be).
 */
export const toolbarClearSx = () => showAtLeast(TOOLBAR_CLEAR_MIN_WIDTH_PX, 'block')

/**
 * The other half of `toolbarClearSx`, and it is not cosmetic — leave it out and the fix is
 * visibly broken rather than silently so.
 *
 * `Filters` and `Sort` both square off their button's RIGHT corners while applied, so the
 * button and the `X` beside it read as one segmented control. Once the `X` collapses, that
 * squared edge is a flat side with nothing attached to it — a control that looks cut in half.
 * So the squaring has to be gated on the same query the `X` is, not on the applied flag.
 *
 * Spread into the ButtonGroup's `& > button:first-of-type` rule, guarded by `applied` at the
 * call site (an unapplied control has no `X` at any width and stays fully rounded).
 */
export const toolbarClearEdgeSx = {
  borderTopRightRadius: 8,
  borderBottomRightRadius: 8,
  [`@container ${TOOLBAR_CONTAINER_NAME} (min-width:${TOOLBAR_CLEAR_MIN_WIDTH_PX}px)`]: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
} as const
